import { AdminReceivingPlanPanel } from '@features/receiving-plan/ui/AdminReceivingPlanPanel'
import { AdminReferralsPanel } from '@features/referrals/ui/AdminReferralsPanel'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { CheckoutBuilder } from '@features/checkout-builder'
import { useAdminRuntimeCapabilities, useAdminUser } from '@features/admin/api/admin.api'
import type { ProductItem } from '@features/admin/api/admin.api'
import {
  AdminProductEditRequestDrawer,
  type AdminProductEditApproval,
} from '@features/admin/ui/AdminProductEditRequestDrawer'
import { AdminUserDetailScreen } from '@features/admin/ui/AdminUserDetailScreen'
import { ProductEditor } from '@features/product/ui/ProductEditor'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'

type ProductEditRequest = {
  product: ProductItem
  producerEmail: string
  producerName?: string
}

export function UsuarioDetalhePage() {
  const referralsEnabled = useFeatureFlag('referralProgram')
  const { userId } = useParams({ from: '/protected/usuarios/$userId' })
  const session = useSession((state) => state.user)
  const canRead = hasAdminPermission(session, 'USER_READ')
  const user = useAdminUser(canRead ? userId : null)
  const runtimeCapabilities = useAdminRuntimeCapabilities()
  const [request, setRequest] = useState<ProductEditRequest | null>(null)
  const [editorTarget, setEditorTarget] = useState<{
    product: ProductItem
    approval: AdminProductEditApproval
  } | null>(null)
  const [checkoutBuilder, setCheckoutBuilder] = useState<{
    productId: string
    checkoutId?: string
  } | null>(null)
  const detail = user.data?.user ?? null

  return (
    <>
      <AdminUserDetailScreen
        userId={userId}
        user={detail}
        loading={user.isPending && canRead}
        error={user.isError || !canRead}
        canReadProducts={hasAdminPermission(session, 'PRODUCT_READ')}
        canWriteProducts={hasAdminPermission(session, 'PRODUCT_WRITE')}
        canReadRevenue={hasAdminPermission(session, 'REVENUE_READ')}
        canReadSales={hasAdminPermission(session, 'SALE_READ')}
        referralsPanel={
          referralsEnabled && canRead && detail ? (
            <AdminReferralsPanel
              key={userId}
              userId={userId}
              canManagePermission={hasAdminPermission(session, 'USER_MANAGE')}
              canManageRate={hasAdminPermission(session, 'USER_PLAN_MANAGE')}
            />
          ) : undefined
        }
        receivingPlanPanel={
          canRead && detail ? (
            <AdminReceivingPlanPanel
              userId={userId}
              userLabel={detail.name || detail.email}
              canManage={hasAdminPermission(session, 'USER_PLAN_MANAGE')}
            />
          ) : null
        }
        onRetry={() => void user.refetch()}
        onEditProduct={(product) => {
          setRequest({
            product,
            producerEmail: detail?.email ?? '',
            ...(detail?.name ? { producerName: detail.name } : {}),
          })
        }}
      />

      <AdminProductEditRequestDrawer
        key={request?.product.id ?? 'closed'}
        product={request?.product ?? null}
        producerEmail={request?.producerEmail ?? ''}
        producerName={request?.producerName}
        onClose={() => setRequest(null)}
        onApproved={(product, approval) => {
          setRequest(null)
          setEditorTarget({ product, approval })
        }}
      />
      <ProductEditor
        open={editorTarget !== null}
        productId={editorTarget?.product.id}
        adminProducerId={editorTarget?.product.producerId}
        minuteBillingEnabled={runtimeCapabilities.data?.minuteBillingEnabled === true}
        approvalContext={editorTarget?.approval}
        onClose={() => setEditorTarget(null)}
        onOpenCheckoutBuilder={(productId, checkoutId) =>
          setCheckoutBuilder({ productId, ...(checkoutId ? { checkoutId } : {}) })
        }
      />
      <CheckoutBuilder
        open={checkoutBuilder !== null}
        productId={checkoutBuilder?.productId}
        initialCheckoutId={checkoutBuilder?.checkoutId}
        onClose={() => setCheckoutBuilder(null)}
      />
    </>
  )
}
